/**
 * Bank partner registry — single source of truth for bank partners (MV-3049).
 *
 * Banks live in CODE, not Sanity. This file is the source of truth; Sanity
 * gets read-only synced mirror docs (`_id: 'bank.${key}'`) generated from
 * this data via `pnpm studio:generate-bank-registry` +
 * `pnpm studio:sync-banks`, so CMS content can hold a stable `reference` to
 * a bank (e.g. `coBrandedHero.coBrand`, `partnersSection`).
 *
 * To add/change a bank: edit this file, then re-run the two `studio:*`
 * scripts above. Do not hand-edit the mirrored `bank.*` docs in Studio.
 *
 * `logoKey` names an existing ui-content bank-logo asset (see
 * `packages/ui-content/src/features/Partners/Bank/banks.types.ts` — its
 * `BankCode`, backed by `<app>/public/partners/bank/<market>/<code>.svg`).
 * Only DK assets exist today (this registry seeds from the 17 dk-legacy
 * bank pages under `apps/site-dk-legacy/static/banker-og-laangivere/`).
 *
 * `logoKey` mapping was cross-checked against
 * `packages/personalisation-engine/src/reference/bank-names.ts` (warehouse
 * `MARKET|CODE -> lender name` reference — the same LenderID/ShortName
 * space the ui-content `BankCode` comment refers to). Two corrections that
 * came out of that cross-check:
 *  - `DK|SN` is "AcceptLån" (a different lender), NOT Spar Nord — Spar Nord's
 *    code is `DK|SNH` ("Spar Nord Hjorring"). `spar-nord` below uses `SNH`.
 *  - `DK|BB` is the confirmed warehouse code for Basisbank; its `dk/BB.svg`
 *    (and Minifinans' `dk/MF.svg`) were taken from site-dk-legacy's marquee.
 * Entries with `logoKey: ''` have no confirmed matching asset; a wrong logo
 * is worse than none, so these are intentionally blank pending real assets.
 */

import type { BankCode } from './bank-codes';

export type BankMarket = 'dk' | 'no' | 'se' | 'fi';

export type BankRegistryEntry = {
  key: string;
  name: string;
  markets: readonly BankMarket[];
  /**
   * The bank's `BankCode` — the same code MyPage renders offers and logos
   * by (`./bank-codes.ts`; svg in ui-content's `public/partners/bank/`), or
   * '' for a bank with no code/logo. Typed, so a key MyPage can't render
   * doesn't compile; `bank-registry.test.ts` asserts every code of a
   * registry market is covered, so a new MyPage lender can't be missed.
   */
  logoKey: BankCode | '';
};

export const BANK_REGISTRY: readonly BankRegistryEntry[] = [
  { key: 'bank-norwegian', name: 'Bank Norwegian', markets: ['dk'], logoKey: 'BN' },
  {
    key: 'basisbank',
    name: 'Basisbank',
    markets: ['dk'],
    // warehouse code `BB` (packages/personalisation-engine/src/reference/
    // bank-names.ts); dk/BB.svg is the logo site-dk-legacy's marquee shows.
    logoKey: 'BB',
  },
  // DK|SN — see the file-header note (not Spar Nord).
  { key: 'acceptlaan', name: 'AcceptLån', markets: ['dk'], logoKey: 'SN' },
  { key: 'coop-bank', name: 'Coop Bank', markets: ['dk'], logoKey: 'COP' },
  { key: 'express-bank', name: 'Express Bank', markets: ['dk'], logoKey: 'EB' },
  { key: 'facitbank', name: 'Facit Bank', markets: ['dk'], logoKey: 'FB' },
  { key: 'ferratum', name: 'Ferratum', markets: ['dk'], logoKey: 'FR' },
  { key: 'ikano-bank', name: 'Ikano Bank', markets: ['dk'], logoKey: 'IK' },
  { key: 'leasy', name: 'Leasy', markets: ['dk'], logoKey: 'LEN' },
  {
    key: 'minifinans',
    name: 'Minifinans',
    markets: ['dk'],
    // No warehouse code in the reference — `MF` is the asset name only.
    // dk/MF.svg is the logo site-dk-legacy's marquee shows (the older
    // `MiniFinans-disabled` asset stays unreferenced).
    logoKey: 'MF',
  },
  { key: 'nordea', name: 'Nordea', markets: ['dk'], logoKey: 'ND' },
  { key: 'nordfyns-bank', name: 'Nordfyns Bank', markets: ['dk'], logoKey: 'NF' },
  {
    key: 'pensam-bank',
    name: 'PenSam Bank',
    markets: ['dk'],
    // TODO(MV-3049): no confirmed dk logo asset for PenSam Bank.
    logoKey: '',
  },
  { key: 'remember', name: 're:member', markets: ['dk'], logoKey: 'RE' },
  { key: 'resurs-bank', name: 'Resurs Bank', markets: ['dk'], logoKey: 'RB' },
  {
    key: 'santander',
    name: 'Santander',
    markets: ['dk'],
    // TODO(MV-3049): no confirmed dk logo asset for Santander — the
    // warehouse reference only has NO|SCB and SE|ST, no DK|* code.
    logoKey: '',
  },
  // DK|SNH ("Spar Nord Hjorring") is the confirmed code, not DK|SN
  // ("AcceptLån" — a different lender). See file-header note.
  { key: 'spar-nord', name: 'Spar Nord', markets: ['dk'], logoKey: 'SNH' },
  { key: 'sparxpres', name: 'Sparxpres', markets: ['dk'], logoKey: 'SX' },
  { key: 'tf-bank', name: 'TF Bank', markets: ['dk'], logoKey: 'TF' },
  { key: 'unilaan', name: 'Unilån', markets: ['dk'], logoKey: 'UL' },
] as const;

export function getBankByKey(key: string): BankRegistryEntry | undefined {
  return BANK_REGISTRY.find((bank) => bank.key === key);
}
